import { Button, Col, Tooltip, notification } from 'antd'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import CustomSelect from 'src/app/shared/components/common/select'
import CustomSelectAll from 'src/app/shared/components/common/select-all'
import { USER_ROLE } from 'src/app/shared/constants/app.constants'
import { useWindowSize } from 'src/app/shared/hooks/useWindowSize'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import { getUserProfile } from 'src/app/store/auth'
import ManualNotificationModal from '../ManualNotificationModal'

export interface IAccount {
  accountName: string
  id: string
}

export interface IClient {
  id: string
  clientName: string
}

interface Props {
  selectedClients: any
  setSelectedClients: React.Dispatch<any>
  selectedAccounts: any
  setSelectedAccounts: React.Dispatch<any>
  handleGetClients: () => Promise<any>
  handleGetClientInfoByUser: () => Promise<any>
  clients: IClient[]
  handleGetAccounts: (clientIds: string[]) => Promise<IAccount[]>
  accounts: IAccount[]
  loadingClients: boolean
  loadingAccounts: boolean
}

function SearchArea(props: Props) {
  const {
    selectedClients,
    selectedAccounts,
    setSelectedClients,
    setSelectedAccounts,
    handleGetClients,
    handleGetClientInfoByUser,
    clients,
    handleGetAccounts,
    accounts,
    loadingClients,
    loadingAccounts,
  } = props
  const { role } = useSelector(getUserProfile)
  const { mode } = useWindowSize()
  const { t } = useTranslation()
  const { userPermissions } = useRolePermission()
  const [api, contextHolder] = notification.useNotification()
  const [isManualNotificationModalOpen, setIsManualNotificationModalOpen] = useState<boolean>(false)

  useEffect(() => {
    if (role !== USER_ROLE.CLIENT) {
      handleGetClients()
      return
    }
    handleGetClientInfoByUser()
  }, [role, handleGetClientInfoByUser, handleGetClients])

  useEffect(() => {
    // User selects all clients
    let clientIds = []
    if (selectedClients?.includes(null)) clientIds = clients?.map((item: any) => item?.id)
    else {
      clientIds = selectedClients
    }

    if (clientIds?.length) handleGetAccounts(clientIds)
  }, [selectedClients, clients, handleGetAccounts])

  const clientOptions =
    clients?.map((item: any) => ({
      label: item?.clientName,
      value: item?.id,
    })) ?? []

  const accountOptions =
    accounts?.map((item: any) => ({
      label: item?.accountName,
      value: item?.id,
    })) ?? []

  const handleChangeClients = (data: any) => {
    setSelectedAccounts([null])
    if (!data || data?.length === 0) {
      setSelectedClients([null])
    } else if (data?.includes(null)) {
      if (!data[0]) {
        const newData = data?.slice(1)
        setSelectedClients([...newData])
      } else {
        setSelectedClients([null])
      }
    } else {
      setSelectedClients(data)
    }
  }

  const handleChangeAccounts = (data: any) => {
    if (!data || data?.length === 0) {
      setSelectedAccounts([null])
    } else if (data?.includes(null)) {
      if (!data[0]) {
        const newData = data?.slice(1)
        setSelectedAccounts([...newData])
      } else {
        setSelectedAccounts([null])
      }
    } else {
      setSelectedAccounts(data)
    }
  }

  return (
    <>
      <Col
        xs={{
          span: 12,
        }}
        md={{ span: 8 }}
        className='select-client-container'
      >
        {role === USER_ROLE.CLIENT ? (
          <CustomSelect
            showSearch={true}
            options={clientOptions}
            value={
              clients?.map((item: any) => ({
                label: item.clientName,
                value: item.id,
              }))?.[0]
            }
            loading={loadingClients}
            disabled={true}
          />
        ) : (
          <CustomSelectAll
            maxTagCount={'responsive'}
            maxTagPlaceholder={(omittedValues) => (
              <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                <span>+ {omittedValues.length}...</span>
              </Tooltip>
            )}
            options={clientOptions}
            defaultActiveFirstOption
            defaultOption={{
              label: 'All Clients',
              value: null,
            }}
            value={selectedClients}
            onChange={handleChangeClients}
            disabled={loadingClients}
            loading={loadingClients}
            haveAllOption={true}
            showSearch
            allowClear={!selectedClients.includes(null)}
          />
        )}
      </Col>
      <Col
        xs={{
          span: 12,
        }}
        md={{ span: 8 }}
      >
        <CustomSelectAll
          maxTagCount={'responsive'}
          maxTagPlaceholder={(omittedValues) => (
            <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
              <span>+ {omittedValues.length}...</span>
            </Tooltip>
          )}
          options={accountOptions}
          defaultActiveFirstOption
          defaultOption={{
            label: 'All Accounts',
            value: null,
          }}
          value={selectedAccounts}
          onChange={handleChangeAccounts}
          loading={loadingAccounts}
          disabled={loadingAccounts || loadingClients}
          haveAllOption={true}
          showSearch
          allowClear={!selectedAccounts.includes(null)}
        />
      </Col>
      {userPermissions[ESubModules.EXCEPT_PROCESS]?.includes(EPermissions.SEND_MANUALLY_EXCEPT_NOTIF) && (
        <Col
          xs={{
            span: 12,
          }}
          md={{ span: 8 }}
        >
          {contextHolder}
          {/* size='large' matches the height of the client/account selects on the same row */}
          <Button size='large' type='primary' onClick={() => setIsManualNotificationModalOpen(true)}>
            {t('QuickExceptionProcessing:button.sendExceptionNotifications')}
          </Button>
          <ManualNotificationModal
            isOpen={isManualNotificationModalOpen}
            onClose={() => setIsManualNotificationModalOpen(false)}
            api={api}
          />
        </Col>
      )}
    </>
  )
}

export default SearchArea
